import { createRoot } from "react-dom/client";
import { App } from "components/App";
import { APP_CONTAINER_CLASSNAME, APP_ROOT_CLASSNAME, POPUP_CLASSNAME } from "constants/common.constants";
import { PageNameProvider } from "context/PageName.context";
import { configureRecaptcha, createRecaptchaScriptElement } from "utils/recaptcha";
import { APP_FONTS_REGISTER_STYLESHEET_HREF, APP_FONTS_STYLESHEET_HREF, APP_STYLESHEET_HREF } from "utils/url.utils";

const createInitialNodes = (): {
  appContainer: HTMLDivElement;
  appRoot: HTMLDivElement;
  modalNode: HTMLDivElement;
  appStylesheetNode: HTMLLinkElement;
  fontsRegisterStylesheetNode: HTMLLinkElement;
  fontsStylesheetNode: HTMLLinkElement;
} => {
  const appContainer = document.createElement("div");
  appContainer.classList.add(APP_CONTAINER_CLASSNAME);
  appContainer.setAttribute("data-version", __APP_VERSION__);

  const appRoot = document.createElement("div");
  appRoot.classList.add(APP_ROOT_CLASSNAME);

  const modalNode = document.createElement("div");
  modalNode.classList.add(POPUP_CLASSNAME);

  const appStylesheetNode = document.createElement("link");
  appStylesheetNode.type = "text/css";
  appStylesheetNode.rel = "stylesheet";
  appStylesheetNode.href = APP_STYLESHEET_HREF;

  const fontsRegisterStylesheetNode = document.createElement("link");
  fontsRegisterStylesheetNode.type = "text/css";
  fontsRegisterStylesheetNode.rel = "stylesheet";
  fontsRegisterStylesheetNode.href = APP_FONTS_REGISTER_STYLESHEET_HREF;

  const fontsStylesheetNode = document.createElement("link");
  fontsStylesheetNode.type = "text/css";
  fontsStylesheetNode.rel = "stylesheet";
  fontsStylesheetNode.href = APP_FONTS_STYLESHEET_HREF;

  return {
    appContainer,
    appRoot,
    modalNode,
    appStylesheetNode,
    fontsRegisterStylesheetNode,
    fontsStylesheetNode,
  };
};

const main = () => {
  const appRootNode: HTMLElement | null = document.getElementById("contact_us_root");
  if (!appRootNode) return;

  const recaptchaSiteKey = appRootNode.getAttribute("data-recaptcha-site-key");
  if (recaptchaSiteKey) {
    configureRecaptcha(recaptchaSiteKey);
  }

  const pageName = appRootNode.dataset.pageName;
  if (!pageName) {
    throw new Error("Page name is not set");
  }

  const { appContainer, appRoot, modalNode, appStylesheetNode, fontsRegisterStylesheetNode, fontsStylesheetNode } =
    createInitialNodes();

  // Assets are only awaited so they are *settled*: a failure on either must not
  // leave the app unrendered. Listeners are attached before the nodes are inserted,
  // because an already-cached asset can otherwise settle before we start listening
  // and its load event would then never be observed.
  let appRendered = false;
  let stylesheetSettled = false;
  let recaptchaSettled = !recaptchaSiteKey;

  const renderApp = () => {
    createRoot(appRoot).render(
      <PageNameProvider value={{ pageName }}>
        <App />
      </PageNameProvider>
    );
  };

  const tryRender = () => {
    if (appRendered || !stylesheetSettled || !recaptchaSettled) return;
    appRendered = true;
    renderApp();
  };

  const settleStylesheet = () => {
    stylesheetSettled = true;
    tryRender();
  };

  const settleRecaptcha = () => {
    recaptchaSettled = true;
    tryRender();
  };

  if (!__DEV_SERVER_LAUNCHED__) {
    appStylesheetNode.addEventListener("load", settleStylesheet);
    appStylesheetNode.addEventListener("error", settleStylesheet);
  }

  let recaptchaScript: HTMLScriptElement | null = null;
  if (recaptchaSiteKey) {
    recaptchaScript = createRecaptchaScriptElement(recaptchaSiteKey);
    recaptchaScript.addEventListener("load", settleRecaptcha);
    recaptchaScript.addEventListener("error", settleRecaptcha);
  }

  appRootNode.appendChild(fontsRegisterStylesheetNode);
  appContainer.appendChild(fontsStylesheetNode);

  appContainer.appendChild(appRoot);
  appContainer.appendChild(modalNode);

  if (!__DEV_SERVER_LAUNCHED__) {
    appContainer.appendChild(appStylesheetNode);
  }

  appRootNode.appendChild(appContainer);

  if (recaptchaScript) {
    appRootNode.appendChild(recaptchaScript);
  }

  if (__DEV_SERVER_LAUNCHED__) {
    stylesheetSettled = true;
    recaptchaSettled = true;
  }

  tryRender();
};

if (__DEV_SERVER_LAUNCHED__) {
  main();
} else {
  (function checkForInit() {
    if (document.readyState === "complete") {
      return main();
    }
    window.requestAnimationFrame(() => {
      checkForInit();
    });
  })();
}
